import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { secureApi, tokenStorage, hasPermission } from '../utils/auth';
import SecurityAlert from '../components/SecurityAlert';

const APIKeysManager = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    expires_days: 365
  });
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const user = tokenStorage.getUser();
  const canUseAPIKeys = hasPermission('api_keys');

  useEffect(() => {
    if (canUseAPIKeys) {
      loadAPIKeys();
    }
  }, [canUseAPIKeys]);

  const loadAPIKeys = async () => {
    try {
      const data = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/api-keys`
      );
      setApiKeys(data.api_keys || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load API keys' });
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const data = await secureApi.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/api-keys`,
        formData
      );
      
      setNewKey(data);
      setShowCreateForm(false);
      setFormData({ name: '', expires_days: 365 });
      loadAPIKeys();
      
      setMessage({ 
        type: 'success', 
        text: 'API key created successfully. Make sure to copy it now - it won\'t be shown again!' 
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to create API key' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (keyId, keyName) => {
    if (!window.confirm(`Delete API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await secureApi.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/api-keys/${keyId}`
      );
      
      setMessage({ type: 'success', text: 'API key revoked successfully' });
      loadAPIKeys();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to revoke API key' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  if (!canUseAPIKeys) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <SecurityAlert
            type="warning"
            title="Pro Feature"
            message="API keys are available for Pro, Enterprise, and Defense tier users. Upgrade your account to access programmatic API access."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
                <p className="text-gray-600">Manage your programmatic access keys</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Create Key
            </button>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <SecurityAlert
            type={message.type}
            message={message.text}
            onDismiss={() => setMessage(null)}
          />
        )}

        {/* New Key Display */}
        {newKey && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-green-900">Your new API key</h3>
                <p className="text-sm text-green-700 mt-1">
                  Make sure to copy this key now. You won't be able to see it again!
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono text-gray-900 break-all">
                  {newKey.api_key}
                </code>
                <button
                  onClick={() => copyToClipboard(newKey.api_key)}
                  className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                >
                  {copiedKey ? 'Copied!' : <><Copy className="h-4 w-4" /> Copy</>}
                </button>
              </div>
            </div>
            
            <button
              onClick={() => setNewKey(null)}
              className="mt-4 text-sm text-green-700 hover:text-green-800 font-medium"
            >
              I've saved my key, dismiss this message
            </button>
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && !newKey && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New API Key</h2>
            
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Production API Key"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Choose a descriptive name to identify this key</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration (days)
                </label>
                <select
                  value={formData.expires_days}
                  onChange={(e) => setFormData({ ...formData, expires_days: Number(e.target.value) || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                  <option value="">Never expire</option>
                </select>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Key'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Usage Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">How to use API keys</h3>
          <p className="text-sm text-blue-800 mb-3">
            Include your API key in the request header:
          </p>
          <div className="bg-white rounded border border-blue-200 p-3">
            <code className="text-sm font-mono text-gray-900">
              curl -H "X-API-Key: your_key_here" {process.env.REACT_APP_BACKEND_URL}/api/endpoint
            </code>
          </div>
        </div>

        {/* API Keys List */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Your API Keys</h2>
          
          {apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No API keys yet</p>
              <p className="text-sm text-gray-500 mt-1">Create your first API key to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900">{key.name}</p>
                      {!key.active && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                          Revoked
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>Prefix: <code className="font-mono">{key.key_prefix}...</code></span>
                      <span>•</span>
                      <span>Created: {formatDate(key.created_at)}</span>
                      {key.expires_at && (
                        <>
                          <span>•</span>
                          <span>Expires: {formatDate(key.expires_at)}</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Last used: {key.last_used ? formatDate(key.last_used) : 'Never'} • 
                      Used {key.usage_count || 0} times
                    </div>
                  </div>
                  
                  {key.active && (
                    <button
                      onClick={() => handleDeleteKey(key.id, key.name)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Security Best Practices</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Never share your API keys or commit them to version control</li>
                <li>Rotate your keys regularly (every 90 days recommended)</li>
                <li>Use different keys for different environments (dev, staging, production)</li>
                <li>Revoke keys immediately if they are compromised</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIKeysManager;
